/**
 * The review page's keys (curation-ui.md CU-8). Keys never fire inside text fields, and
 * every key has a visible button. `1`, `2`, `3` (keep, merge, drop) belong to the
 * candidate block of the term in focus. Pure.
 */
export type ReviewKeyAction =
  | "nextUnit"
  | "previousUnit"
  | "nextUnresolved"
  | "previousUnresolved"
  | "markOk"
  | "markNeedsChange"
  | "comment"
  | "toggleTechnical"
  | "filter"
  | "toggleOutline"
  | "toggleContext"
  | "approve"
  | "requestChanges"
  | "merge"
  | "help"
  | "close";

const KEYS: Readonly<Record<string, ReviewKeyAction>> = {
  j: "nextUnit",
  ArrowDown: "nextUnit",
  k: "previousUnit",
  ArrowUp: "previousUnit",
  n: "nextUnresolved",
  N: "previousUnresolved",
  o: "markOk",
  x: "markNeedsChange",
  c: "comment",
  t: "toggleTechnical",
  "/": "filter",
  "[": "toggleOutline",
  "]": "toggleContext",
  a: "approve",
  r: "requestChanges",
  m: "merge",
  "?": "help",
  Escape: "close",
};

/** The keys as the help lists them, with the key under `reviews.curation.keys` that names them. */
export const KEY_HELP: readonly (readonly [string, string])[] = [
  ["j / k", "units"],
  ["n / Shift+n", "unresolved"],
  ["o", "ok"],
  ["x", "needsChange"],
  ["c", "comment"],
  ["t", "technical"],
  ["1 / 2 / 3", "resolve"],
  ["/", "filter"],
  ["[ / ]", "columns"],
  ["a / r / m", "decide"],
  ["?", "help"],
  ["Esc", "close"],
];

export function isTypingTarget(target: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

export interface KeyPress {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly target: EventTarget | null;
}

/** The action for a key press, or `null`: modified keys and keys typed into fields are left alone. */
export function reviewKeyAction(press: KeyPress): ReviewKeyAction | null {
  if (press.ctrlKey || press.metaKey || press.altKey) {
    return null;
  }
  if (press.key !== "Escape" && isTypingTarget(press.target)) {
    return null;
  }
  return KEYS[press.key] ?? null;
}
